import React, { InputHTMLAttributes, forwardRef, useId } from 'react';
import Label from './Label';
import Error from './Error';
import clsx from 'clsx';
import { get, useFormState } from 'react-hook-form';

export interface CheckboxInputFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  hideLabel?: boolean;
  description?: string;
  additionalContainerClasses?: string;
}

export const CheckboxInputField = forwardRef<HTMLInputElement, CheckboxInputFieldProps>(
  function InternalCheckboxInputField(
    { label, id, additionalContainerClasses, description, className, hideLabel, ...props },
    ref
  ) {
    const { name } = props;
    const formState = useFormState();
    const error = get(formState.errors, name);
    const defaultId = useId();
    id = id ?? defaultId;

    return (
      <div
        className={clsx('flex flex-col w-full xs:col-span-2', additionalContainerClasses)}
        id={id}
      >
        <div className="flex items-center">
          <input
            type="checkbox"
            ref={ref}
            id={`${id}::input`}
            className={clsx(
              `peer shrink-0 appearance-none relative w-5 h-5 p-2  border dark:border-grey-600 border-grey-500 rounded-md  
          
          after:absolute 
          after:left-0 after:top-0 after:h-full after:w-full  checked:after:bg-[url('data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9JzMwMHB4JyB3aWR0aD0nMzAwcHgnICBmaWxsPSIjZmZmZmZmIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgdmVyc2lvbj0iMS4xIiB4PSIwcHgiIHk9IjBweCI+PHRpdGxlPmljb25fYnlfUG9zaGx5YWtvdjEwPC90aXRsZT48ZGVzYz5DcmVhdGVkIHdpdGggU2tldGNoLjwvZGVzYz48ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48ZyBmaWxsPSIjZmZmZmZmIj48ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgyNi4wMDAwMDAsIDI2LjAwMDAwMCkiPjxwYXRoIGQ9Ik0xNy45OTk5ODc4LDMyLjQgTDEwLjk5OTk4NzgsMjUuNCBDMTAuMjI2Nzg5MSwyNC42MjY4MDE0IDguOTczMTg2NDQsMjQuNjI2ODAxNCA4LjE5OTk4Nzc5LDI1LjQgTDguMTk5OTg3NzksMjUuNCBDNy40MjY3ODkxNCwyNi4xNzMxOTg2IDcuNDI2Nzg5MTQsMjcuNDI2ODAxNCA4LjE5OTk4Nzc5LDI4LjIgTDE2LjU4NTc3NDIsMzYuNTg1Nzg2NCBDMTcuMzY2ODIyOCwzNy4zNjY4MzUgMTguNjMzMTUyOCwzNy4zNjY4MzUgMTkuNDE0MjAxNCwzNi41ODU3ODY0IEw0MC41OTk5ODc4LDE1LjQgQzQxLjM3MzE4NjQsMTQuNjI2ODAxNCA0MS4zNzMxODY0LDEzLjM3MzE5ODYgNDAuNTk5OTg3OCwxMi42IEw0MC41OTk5ODc4LDEyLjYgQzM5LjgyNjc4OTEsMTEuODI2ODAxNCAzOC41NzMxODY0LDExLjgyNjgwMTQgMzcuNzk5OTg3OCwxMi42IEwxNy45OTk5ODc4LDMyLjQgWiI+PC9wYXRoPjwvZz48L2c+PC9nPjwvc3ZnPg==')] after:bg-[length:40px] after:bg-center after:bg-no-repeat after:content-[''] checked:bg-blue-600 checked:dark:bg-blue-600
          hover:ring hover:dark:ring-blue-500

          focus:ring focus:dark:ring-blue-500
          focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none 
          `,
              {
                'border-red-500 focus:border-red-300': !!error,
              },
              className
            )}
            {...props}
          />

          {label ? (
            <Label
              htmlFor={`${id}::input`}
              description={description}
              additionalClasses="ml-2"
              hideLabel={hideLabel}
            >
              {label}
            </Label>
          ) : null}
        </div>
        <Error error={error} />
      </div>
    );
  }
);
